import { useLocationSearch } from '@rocketlane/rocket-router';
import {
  QueryClient,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { proxy as comlinkProxy } from 'comlink';
import { Dictionary, isEmpty, keyBy, unionBy, uniq } from 'lodash';
import _flatMap from 'lodash/flatMap';
import _keyBy from 'lodash/keyBy';
import _unionBy from 'lodash/unionBy';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  ProjectId,
  Team,
  TeamWithMeta,
  User,
  UserStatus,
  UserType,
} from 'api/models';
import { ProjectAPI } from 'api/project';
import { BulkOrgChartUpdate, UserAPI } from 'api/user';
import { toastService } from 'components/molecules/toast';
import {
  AssigneePickerData,
  FetchAssigneePickerDataParams,
  Member,
  MembersAPI,
  MembersResponse,
  MembersV2Response,
  TeamsWithMembersResponse,
  TeamWithProjectData,
  UpdateExternalMembers,
  UpdateMemberIntegrationsPayload,
  MemberIntegrationsResponse,
  TeamsResponse,
} from 'data/members/api';
import { invalidateSwrUser } from 'hooks/use-swr-user-hooks';
import { Translate, useTranslate } from 'hooks/use-translation';
import {
  getProjectMembersQueryKey,
  queryClient as queryClientDefault,
  useProjectMembersQuery,
} from 'queries';
import { Company } from 'stores/companies';
import { putAll as putAllTeams } from 'stores/teams';
import { putAll as putAllUsers } from 'stores/users';
import { ActiveUsersAndTeams } from 'utils/active-users-and-teams';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { userOption } from 'utils/project-fields/construct-user-option';
import { oneDay, oneHour } from './constants';
import {
  getPeopleHierarchyQueryKey,
  getPeopleListQueryKey,
} from './customer-company';
import { useHideNonStrategicPartner } from './partner';
import {
  getProjectPlaceholdersQueryKey,
  getProjectPlaceholdersWithMetaQueryKey,
} from './placeholder';

export const MEMBERS_V2_QUERY_KEY = ['members-v2'];
export const TEAMS_QUERY_KEY = ['project-teams'];
export const MEMBERS_V2_MODIFIED_QUERY_KEY = ['members-v2-modified'];
export const TEAMS_WITH_MEMBERS_QUERY_KEY = ['teams-with-members'];

export const ALL_MEMBERS_QUERY_KEY = ['all-members'];
export const ALL_MEMBERS_MAP_QUERY_KEY = ['all-members-map'];
export const ASSIGNE_PICKER_DATA_QUERY_KEY = (searchString: string) => [
  'assignee-picker-data',
  searchString,
];

type BaseMembersQueryOptions<TData = unknown> = UseQueryOptions<
  MembersResponse,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type BaseMembersV2QueryOptions<TData = unknown> = UseQueryOptions<
  MembersV2Response,
  unknown,
  TData,
  typeof ALL_MEMBERS_QUERY_KEY
>;

export type BaseTeamsWithMembersQueryOptions<TData = unknown> = UseQueryOptions<
  TeamsWithMembersResponse,
  unknown,
  TData,
  typeof TEAMS_WITH_MEMBERS_QUERY_KEY
>;

export type MembersQueryOptions = BaseMembersQueryOptions<MembersResponse>;
type MembersV2QueryOptions = BaseMembersV2QueryOptions<MembersV2Response>;
type TeamsWithMembersQueryOptions =
  BaseTeamsWithMembersQueryOptions<TeamsWithMembersResponse>;

export type MembersQueryResult = UseQueryResult<MembersResponse, unknown>;
type MembersV2QueryResult = UseQueryResult<MembersV2Response, unknown>;
type TeamsWithMembersQueryResult = UseQueryResult<
  TeamsWithMembersResponse,
  unknown
>;

export const BaseMembersQueryOptions: Pick<
  BaseMembersQueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: ALL_MEMBERS_QUERY_KEY,
  queryFn: () => MembersAPI.findAll(),
  staleTime: oneDay,
};

const BaseMembersV2QueryOptions: Pick<
  BaseMembersV2QueryOptions,
  'queryKey' | 'queryFn' | 'staleTime'
> = {
  queryKey: MEMBERS_V2_QUERY_KEY,
  staleTime: oneHour,
};

const BaseTeamsWithMembersQueryOptions: Pick<
  BaseTeamsWithMembersQueryOptions,
  'queryKey' | 'staleTime'
> = {
  queryKey: TEAMS_WITH_MEMBERS_QUERY_KEY,
  staleTime: oneHour,
};

export const activeUsersAndTeamsFromIndexedB = new ActiveUsersAndTeams();

// @TODO: This need to be moved as a custom hook and called inside a component.
// @TODO: Remove the react query call and move the new memebers api call inside the worker.
function processMembersDataInWorker(
  documents: {
    users: User[];
    teams: Team[];
  },
  onDataSeededIntoIndexedDB?: () => void,
) {
  const worker = callWorkerWithCleanup();

  worker.workerApi.processMembersData(
    documents,
    comlinkProxy(({ users, teams }) => {
      activeUsersAndTeamsFromIndexedB.setActiveUsersAndTeams = {
        users,
        teams,
      };
      onDataSeededIntoIndexedDB?.();
      // clean up worker after members and teams data inserted
      worker.cleanup();
    }),
  );
}

type TeamWithProjectFinancialsBudgetIds = Team & {
  projectFinancialsBudgetIds?: number[];
};

const transformTeams = (teams: Team[]): Team[] => {
  return teams.map((team) => {
    const { projectFinancialsBudgetIds } =
      team as TeamWithProjectFinancialsBudgetIds;
    // projectFinancialsBudgetIds is optional from API; fall back to team.budgetIds or []
    const budgetIds = projectFinancialsBudgetIds ?? team.budgetIds ?? [];

    return {
      ...team,
      budgetIds,
    };
  });
};

const transformMembers = (
  data: MembersV2Response,
  isCurrentUserCustomer: () => boolean,
  updateCompanyIfNonStrategic: (company: Company) => Company,
) => {
  const suggestionsData = [...Object.values(data?.users ?? {})];

  const membersData: Array<User> = suggestionsData.map((user: User) => ({
    ...user,
    userId: user.userId,
  }));

  const users = isCurrentUserCustomer()
    ? membersData.map((member) => ({
        ...member,
        company: updateCompanyIfNonStrategic(member.company),
      }))
    : membersData;
  const teams = transformTeams(Object.values(data?.teams ?? {}));

  return { users, teams };
};

/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */
export const useMembersV2Query = (
  {
    queryKey,
    queryFn,
    staleTime,
    select,
    ...restOptions
  }: MembersV2QueryOptions,
  onDataSeededIntoIndexedDB?: () => void,
): MembersV2QueryResult => {
  // console.log(
  //   '🟣 useMembersV2Query 1 called from:',
  //   new Error().stack?.split('\n')[2],
  // );
  const dispatch = useDispatch();
  const search = useLocationSearch();
  const queryParams = new URLSearchParams(search);
  const isCustomerPreview = !!queryParams.get('customer-preview');
  const {
    isDependentEntitiesLoading,
    isCurrentUserCustomer,
    updateCompanyIfNonStrategic,
  } = useHideNonStrategicPartner();

  return useQuery({
    ...BaseMembersV2QueryOptions,
    queryFn: () =>
      MembersAPI.getMembersV2().then((data) => {
        // These dispatch methods were moved from app-boot-data.tsx after removing the api call to /members on page load
        const { users, teams } = transformMembers(
          data,
          isCurrentUserCustomer,
          updateCompanyIfNonStrategic,
        );

        dispatch(putAllUsers([]));
        dispatch(putAllTeams(teams ?? []));

        queryClientDefault.setQueriesData(MEMBERS_V2_QUERY_KEY, data);

        // In project customer preview we don't want to re-populate the indexDb with just members of a specific project.
        if (isCustomerPreview) {
          onDataSeededIntoIndexedDB?.();
        } else {
          // Handle storing data to indexeddb inside the web worker
          processMembersDataInWorker(
            { teams, users },
            onDataSeededIntoIndexedDB,
          );
        }

        return data;
      }),
    enabled: !isDependentEntitiesLoading,
    ...restOptions,
    refetchOnWindowFocus: false,
  });
};

export const membersV2TransformedQueryFn = async (
  isCurrentUserCustomer: () => boolean,
  updateCompanyIfNonStrategic: (company: Company) => Company,
) => {
  let data = queryClientDefault.getQueryData(MEMBERS_V2_QUERY_KEY) as
    | MembersV2Response
    | undefined;

  if (!data) {
    data = await MembersAPI.getMembersV2();

    if (!data) {
      return {};
    }
  }

  const { users, teams } = transformMembers(
    data,
    isCurrentUserCustomer,
    updateCompanyIfNonStrategic,
  );

  return {
    users,
    teams,
  };
};

export const teamsTransformedQueryFn = async (
  isCurrentUserCustomer: () => boolean,
  updateCompanyIfNonStrategic: (company: Company) => Company,
) => {
  let data = queryClientDefault.getQueryData(TEAMS_QUERY_KEY) as
    | TeamsResponse
    | undefined;

  if (!data) {
    data = await MembersAPI.getTeams();

    if (!data) {
      return {};
    }
  }

  const { teams } = transformMembers(
    {
      teams: data,
      users: {},
    },
    isCurrentUserCustomer,
    updateCompanyIfNonStrategic,
  );

  return teams;
};

export const useMembersV2TransformedQuery = (): UseQueryResult<{
  users: User[];
  teams: Team[];
}> => {
  // console.log(
  //   '🟪 useMembersV2TransformedQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );
  const { isCurrentUserCustomer, updateCompanyIfNonStrategic } =
    useHideNonStrategicPartner();

  return useQuery(
    MEMBERS_V2_MODIFIED_QUERY_KEY,
    () =>
      membersV2TransformedQueryFn(
        isCurrentUserCustomer,
        updateCompanyIfNonStrategic,
      ),
    {},
  );
};

export const useTeamsTransformedQuery = (): UseQueryResult<Team[]> => {
  const { isCurrentUserCustomer, updateCompanyIfNonStrategic } =
    useHideNonStrategicPartner();

  return useQuery(
    TEAMS_QUERY_KEY,
    () =>
      teamsTransformedQueryFn(
        isCurrentUserCustomer,
        updateCompanyIfNonStrategic,
      ),
    {},
  );
};

const transformTeamsWithMembers = (
  data: TeamsWithMembersResponse,
): TeamsWithMembersResponse => {
  const transformedTeamsMap = Object.entries(data.teamsMap).reduce(
    (acc, [teamId, team]) => {
      const budgetIds = team.projectToBudgetsMap
        ? Object.values(team.projectToBudgetsMap).flat()
        : [];

      const projectIds = team.projectToBudgetsMap
        ? Object.keys(team.projectToBudgetsMap).map(Number)
        : team.projectIds || [];

      acc[teamId] = {
        ...team,
        budgetIds,
        projectIds,
      };

      return acc;
    },
    {} as { [teamId: number]: TeamWithProjectData },
  );

  return {
    teamsMap: transformedTeamsMap,
  };
};

// @deprecated
export const useTeamsWithMembersQuery = (
  options: TeamsWithMembersQueryOptions = {},
): TeamsWithMembersQueryResult => {
  return useQuery({
    ...BaseTeamsWithMembersQueryOptions,
    queryFn: () =>
      MembersAPI.getTeamsWithMembers().then(transformTeamsWithMembers),
    ...options,
  });
};

/**
 *
 * @description Use this hook to manage team placeholders in MembersV2Modified query cache.
 */
export const useManageTeamsInProject = () => {
  const addTeamMemberToProject = useCallback(
    (team: Team, projectId: number) => {
      const { users, teams } = queryClientDefault.getQueryData(
        MEMBERS_V2_MODIFIED_QUERY_KEY,
      ) as {
        users: User[];
        teams: Team[];
      };
      const teamToBeModified = teams.find((t) => t.teamId === team.teamId);

      if (!teamToBeModified) {
        return { users, teams };
      }
      const newTeams = teams
        .filter((t) => t.teamId !== team.teamId)
        .concat({
          ...teamToBeModified,
          ...team,
          projectIds: uniq(
            (teamToBeModified.projectIds ?? []).concat([
              ...(team.projectIds ?? []),
              projectId,
            ]),
          ),
        });

      queryClientDefault.setQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY, {
        users,
        teams: newTeams,
      });

      return { users, teams: newTeams };
    },
    [],
  );

  const removeTeamMemberFromProject = useCallback(
    (team: Team, projectId: number) => {
      queryClientDefault.setQueryData<TeamWithMeta[]>(
        getProjectPlaceholdersWithMetaQueryKey(ProjectId(projectId)),
        (old = []) => old.filter((t) => t.teamId !== team.teamId),
      );
      queryClientDefault.setQueryData<Team[]>(
        getProjectPlaceholdersQueryKey(ProjectId(projectId)),
        (old = []) => old.filter((t) => t.teamId !== team.teamId),
      );

      const cacheData = queryClientDefault.getQueryData(
        MEMBERS_V2_MODIFIED_QUERY_KEY,
      ) as { users: User[]; teams: Team[] } | undefined;

      if (cacheData?.teams == null) {
        return {
          users: cacheData?.users ?? [],
          teams: cacheData?.teams ?? [],
        };
      }

      const { users, teams } = cacheData;
      const teamToBeModified = teams.find((t) => t.teamId === team.teamId);

      if (!teamToBeModified) {
        return { users, teams };
      }
      const newTeams = teams
        .filter((t) => t.teamId !== team.teamId)
        .concat({
          ...teamToBeModified,
          ...team,
          projectIds: uniq(
            (teamToBeModified.projectIds ?? []).concat([
              ...(team.projectIds ?? []),
            ]),
          ).filter((t) => t !== projectId),
        });

      queryClientDefault.setQueryData(MEMBERS_V2_MODIFIED_QUERY_KEY, {
        users,
        teams: newTeams,
      });

      return { users, teams: newTeams };
    },
    [],
  );

  return { addTeamMemberToProject, removeTeamMemberFromProject };
};

/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */
export const useMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  // console.log(
  //   '🟨 useMembersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery({
    ...BaseMembersQueryOptions,
    ...restOptions,
  });
};

/**
 * @deprecated
 * DO NOT USE THIS HOOK, IT IS DEPRECATED AND WILL BE REMOVED IN THE FUTURE.
 */
export const useNativeMembersQuery = ({
  queryKey,
  queryFn,
  staleTime,
  select,
  ...restOptions
}: MembersQueryOptions = {}): MembersQueryResult => {
  // console.log(
  //   '🟨 useNativeMembersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery({
    ...BaseMembersQueryOptions,
    select: ({ users, members, ...rest }) => {
      const result: MembersResponse = {
        ...rest,
        users: (users ?? []).filter(
          ({ userType }) => userType === UserType.Native,
        ),
        members: (members ?? []).filter(
          ({ user }) => user.userType === UserType.Native,
        ),
      };

      return result;
    },
    ...restOptions,
  });
};

export const ALL_USERS_QUERY_KEY = ALL_MEMBERS_QUERY_KEY;

/**
 * @deprecated
 */
export const useUsersQuery = (): UseQueryResult<User[]> => {
  // console.log(
  //   '🔵 useUsersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members.map((member) => member.user);
      },
    },
  );
};

/**
 * @deprecated
 * very bad hook, why even do this? why cant the backend handle this?
 */
export const useUsersMapQuery = (): UseQueryResult<Map<number, User>> => {
  // console.log(
  //   '🟨 useUsersMapQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery<
    MembersResponse,
    unknown,
    Map<number, User>,
    typeof ALL_MEMBERS_MAP_QUERY_KEY
  >({
    ...BaseMembersQueryOptions,
    select: (res) => {
      return new Map<number, User>(
        res.members.map((member) => [member.user.userId, member.user]),
      );
    },
  });
};

/**
 * @deprecated
 */
export const useNativeActiveOrInvitedUsersQuery = (): UseQueryResult<
  User[]
> => {
  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(
            ({ userStatus, userType }) =>
              (userStatus === UserStatus.Active ||
                userStatus === UserStatus.Invited) &&
              userType === UserType.Native,
          );
      },
      refetchOnMount: true,
    },
  );
};

export const useMembersFieldsAccessMetaQuery = (): UseQueryResult<
  MembersResponse['fieldsAccessMeta']
> => {
  return useQuery<
    MembersResponse,
    unknown,
    MembersResponse['fieldsAccessMeta'],
    typeof ALL_USERS_QUERY_KEY
  >({
    ...BaseMembersQueryOptions,
    select: (res) => res.fieldsAccessMeta,
  });
};

/**
 * @deprecated
 *
 * ⚠️ MISLEADING NAME: Despite the name, this returns Native + Partner users (excludes only External)
 * For truly Native-only users, use useAsyncNativeAccountUsers() from hooks/use-idb.ts
 *
 * Returns: Native users + Partner users (Active + Invited + Inactive)
 * Excludes: External users only
 *
 * Migration: Use useAsyncNativeAccountUsers() for Native-only or useAsyncAccountUsers() for Native+Partner
 */
export const useNativeUsersQuery = (): UseQueryResult<User[]> => {
  // console.log(
  //   '🔴 useNativeUsersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType }) => userType !== UserType.External);
      },
    },
  );
};

/**
 * ⚠️ MISLEADING NAME: Despite the name, this returns Native + Partner users (excludes only External)
 * For truly Native-only users, use useAsyncNativeAccountUsers() from hooks/use-idb.ts
 *
 * Used for: View management (view restrictions can include Partner users)
 * Returns: Native users + Partner users (excludes External, excludes API keys)
 * Includes: Active + Invited + Inactive (for historical data)
 *
 * @param filterFn - Optional additional filtering
 * @param includedUserIds - Pre-selected user IDs to ensure they're loaded (for edit mode)
 * @param enabled - Control when query runs (lazy loading)
 *
 * Migration Note: Use useAsyncNativeAccountUsers() for Native-only sprint/epic/backlog owners
 */
export const useAsyncNativeUsers = (
  filterFn?: (user: User) => boolean,
  includedUserIds?: number[],
  enabled: boolean = true,
) => {
  const { data: defaultUsers } = useQuery(
    ['async-native-users', filterFn?.toString(), includedUserIds],
    async () => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1,
        startRow: 0,
        endRow: 100,
        includedUserIds, // ← Include pre-selected users
      });

      const allUsers = [
        ...data.inProjectUsers,
        ...data.notInProjectUsers,
        ...(data.includedUsers ?? []), // ← Add included users to result
      ];
      const filteredUsers = allUsers.filter(
        (user) =>
          user.userType !== UserType.External &&
          // Filter out API key users (UUID emails without @)
          (!user.emailId || user.emailId.includes('@')) &&
          (filterFn ? filterFn(user) : true),
      );

      return filteredUsers;
    },
    { enabled },
  );

  const loadOptions = useCallback(
    async (input: string): Promise<User[]> => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1,
        searchString: input,
        startRow: 0,
        endRow: 100,
        // Note: includedUserIds not needed for search - only for initial load
      });

      const allUsers = [...data.inProjectUsers, ...data.notInProjectUsers];

      return allUsers.filter(
        (user) =>
          user.userType !== UserType.External &&
          // Filter out API key users (UUID emails without @)
          (!user.emailId || user.emailId.includes('@')) &&
          (filterFn ? filterFn(user) : true),
      );
    },
    [filterFn],
  );

  return {
    defaultOptions: defaultUsers ?? [],
    loadOptions,
  };
};

export const useAsyncCustomerUsers = (companyId?: number | string) => {
  const numericCompanyId =
    typeof companyId === 'number' ? companyId : undefined;

  const { data: defaultUsers } = useQuery(
    ['async-customer-users', numericCompanyId],
    async () => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1,
        userType: [UserType.External],
        companyId: numericCompanyId,
        startRow: 0,
        endRow: 100,
      });

      return [...data.inProjectUsers, ...data.notInProjectUsers];
    },
    { enabled: !!numericCompanyId },
  );

  const loadOptions = useCallback(
    async (input: string): Promise<User[]> => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1,
        userType: [UserType.External],
        companyId: numericCompanyId,
        searchString: input,
        startRow: 0,
        endRow: 100,
      });

      return [...data.inProjectUsers, ...data.notInProjectUsers];
    },
    [numericCompanyId],
  );

  return {
    defaultOptions: defaultUsers ?? [],
    loadOptions,
  };
};

export const useNativeUserOptions = () => {
  const { data: defaultOptions = [] } = useQuery(
    ['native-user-default-options'],
    async () => {
      const data = await MembersAPI.fetchPaginatedMembersData({
        projectId: -1,
        userStatus: [UserStatus.Active, UserStatus.Invited],
        startRow: 0,
        endRow: 50,
      });

      return [...data.inProjectUsers, ...data.notInProjectUsers]
        .filter((u) => u.userType === UserType.Native)
        .map(userOption);
    },
  );

  const loadOptions = useCallback(async (inputValue: string) => {
    const data = await MembersAPI.fetchPaginatedMembersData({
      searchString: inputValue,
      projectId: -1,
      userStatus: [UserStatus.Active, UserStatus.Invited],
      startRow: 0,
      endRow: 50,
    });

    return [...data.inProjectUsers, ...data.notInProjectUsers]
      .filter((u) => u.userType === UserType.Native)
      .map(userOption);
  }, []);

  return { defaultOptions, loadOptions };
};

export const useActiveOrInvitedNonCustomerUsersQuery = (): UseQueryResult<
  User[]
> => {
  // console.log(
  //   '🟡 useActiveOrInvitedNonCustomerUsersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType, userStatus }) => {
            return (
              userType !== UserType.External &&
              userStatus !== UserStatus.Inactive
            );
          });
      },
    },
  );
};

export const useCustomerUsersQuery = (): UseQueryResult<User[]> => {
  // console.log(
  //   '🟠 useCustomerUsersQuery called from:',
  //   new Error().stack?.split('\n')[2],
  // );

  return useQuery<MembersResponse, unknown, User[], typeof ALL_USERS_QUERY_KEY>(
    {
      ...BaseMembersQueryOptions,
      select: (res) => {
        return res.members
          .map(({ user }) => user)
          .filter(({ userType }) => {
            return userType === UserType.External;
          });
      },
    },
  );
};

const patchUsersByPayload = (previous: MembersResponse, payload: User) => {
  if (!payload) {
    return previous;
  }
  const index = previous.members.findIndex(
    (x) => x.user.userId === payload.userId,
  );

  if (index === -1) {
    return previous;
  }
  const newMembers: MembersResponse = { ...previous };

  newMembers.members[index].user = {
    ...previous.members[index].user,
    ...payload,
    fields: unionBy(
      payload.fields ?? [],
      previous.members[index].user.fields ?? [],
      'fieldId',
    ),
  };

  if (previous.users !== undefined && newMembers.users !== undefined) {
    const userIndex = previous.users.findIndex(
      (x) => x.userId === payload.userId,
    );

    newMembers.users[userIndex] = {
      ...previous.users[userIndex],
      ...payload,
      fields: unionBy(
        payload.fields ?? [],
        previous.members[userIndex].user.fields ?? [],
        'fieldId',
      ),
    };
  }

  return newMembers;
};

const hanldeMutate = async (queryClient: QueryClient) => {
  const previous: MembersResponse | undefined = queryClient.getQueryData(
    ALL_MEMBERS_QUERY_KEY,
  );

  return {
    fallback: previous,
  };
};

const handleOnError = <T>(
  err: Record<string, Record<string, unknown>>,
  _payload: T,
  context:
    | {
        fallback: MembersResponse | undefined;
      }
    | undefined,
  queryClient: QueryClient,
  translate: Translate<string, string>,
) => {
  const { fallback } = context || {};

  if (fallback) {
    queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
  }
  if (err instanceof Error && err?.request?.status === 403) {
    const errorMsg = translate('settings.team_members.edit.error');

    toastService({
      message: errorMsg,
    }).error();
  }
};

export const useUpdateUserQuery = () => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation((payload: User) => UserAPI.save(payload), {
    onMutate: async (payload) => {
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      if (previous) {
        const newMembers = patchUsersByPayload(previous, payload);

        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
      }

      return {
        fallback: previous,
      };
    },
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) =>
      handleOnError(
        err,
        _payload,
        context,
        queryClient,
        translate as Translate<string, string>,
      ),
    onSuccess: (data) => {
      const previous: MembersResponse | undefined = queryClient.getQueryData(
        ALL_MEMBERS_QUERY_KEY,
      );

      if (previous) {
        const newMembers = patchUsersByPayload(previous, data);

        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
      }
      queryClient.invalidateQueries(
        getPeopleHierarchyQueryKey(data.company.companyId),
      );
      queryClient.invalidateQueries(
        getPeopleListQueryKey(data.company.companyId),
      );
      invalidateSwrUser(data.userId);
    },
  });
};

const USER_LOOKUP_KEY = ['USER_TIMEOFF_LOOKUP'];

export const useUserTimeoffLookup = (): UseQueryResult<Dictionary<User>> => {
  const { data: nativeActiveUsers = [] } = useNativeActiveOrInvitedUsersQuery();

  return useQuery(USER_LOOKUP_KEY, () => {
    return keyBy(nativeActiveUsers, 'userId');
  });
};

export const useUserLookupForProject = (
  projectId: number,
): UseQueryResult<Dictionary<User>> => {
  const { data: projectMembers } = useProjectMembersQuery(ProjectId(projectId));

  const query = useQuery([USER_LOOKUP_KEY, 'project', projectId], () => {
    return keyBy(projectMembers?.userList ?? [], 'userId');
  });

  return query;
};

// used inside timeline view for now
export const useUserTimeoffLookupForProject = (
  projectId: number,
): UseQueryResult<Dictionary<User>> => {
  const { data: projectMembers } = useProjectMembersQuery(ProjectId(projectId));

  return useQuery([USER_LOOKUP_KEY, 'timeoff', 'project', projectId], () => {
    // Filter for native active or invited users from project members
    const nativeActiveUsers =
      projectMembers?.members
        ?.map(({ user }) => user)
        .filter(
          ({ userStatus, userType }) =>
            (userStatus === UserStatus.Active ||
              userStatus === UserStatus.Invited) &&
            userType === UserType.Native,
        ) ?? [];

    return keyBy(nativeActiveUsers, 'userId');
  });
};

export const useSmartUserLookup = (
  projectId?: number,
): UseQueryResult<Dictionary<User>> => {
  const query = useQuery([USER_LOOKUP_KEY, 'smart', projectId], async () => {
    if (projectId) {
      const data = await ProjectAPI.getMembers(projectId);

      return keyBy(data?.userList ?? [], 'userId');
    }

    const data = await MembersAPI.findAll();

    return keyBy(data?.members?.map(({ user }) => user) ?? [], 'userId');
  });

  return query;
};

export type PartialMember = Pick<Member, 'user' | 'permissionSet'>;

const patchMembersByPayload = (
  previous: MembersResponse,
  payload: PartialMember,
) => {
  if (!payload) {
    return previous;
  }
  const index = previous.members.findIndex(
    (x) => x.user.userId === payload.user.userId,
  );

  if (index === -1) {
    return previous;
  }

  const newMembers: MembersResponse = JSON.parse(JSON.stringify(previous));
  const existingMember = newMembers.members[index];

  const updatedMember = {
    ...existingMember,
    ...payload,
    user: {
      ...existingMember.user,
      ...payload.user,
      fields: unionBy(
        payload.user.fields ?? [],
        existingMember.user.fields ?? [],
        'fieldId',
      ),
    },
  };

  newMembers.members[index] = updatedMember;

  return newMembers;
};

export const useTeamMemberGridToUpdate = () => {
  const qc = useQueryClient();
  const translate = useTranslate();

  return useMutation(
    (payload: PartialMember) => MembersAPI.updateMember(payload),
    {
      onMutate: async (payload) => {
        const previous: MembersResponse | undefined = qc.getQueryData(
          ALL_MEMBERS_QUERY_KEY,
        );

        if (previous) {
          const newMembers = patchMembersByPayload(previous, payload);

          qc.setQueryData(ALL_MEMBERS_QUERY_KEY, newMembers);
        }

        return {
          fallback: previous,
        };
      },
      onError: (
        err: Record<string, Record<string, unknown>>,
        _payload,
        context,
      ) => {
        const { fallback } = context || {};

        if (fallback) {
          qc.setQueryData(ALL_MEMBERS_QUERY_KEY, fallback);
        }
        if (err instanceof Error) {
          const errorMsg = translate('settings.team_members.edit.error');

          toastService({
            message: errorMsg,
          }).error();
        }
      },
      onSuccess: (_data, variables) => {
        qc.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        invalidateSwrUser(variables.user.userId);
      },
    },
  );
};

export const getProjectMembersQueryKeyV2 = ([, projectId]: ProjectId): [
  string,
  number,
] => ['squad', projectId];

export const useProjectMembersInSettingsPageQuery = (
  projectId: ProjectId,
  options = {},
) =>
  useQuery(
    getProjectMembersQueryKey(projectId),
    () => ProjectAPI.getMembers(projectId[1]),
    {
      ...options,
    },
  );

export const useUpdateExternalMembersMutation = (): UseMutationResult<
  any, // response type
  unknown,
  UpdateExternalMembers
> => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload) => MembersAPI.updateExternalMembers(payload),
    onSuccess: (_, variables) => {
      const ProjectID = (id: number): ProjectId => ['project_id', id];

      qc.invalidateQueries(
        getProjectMembersQueryKeyV2(ProjectID(variables.projectId)),
      );
    },
  });
};

export const useBulkUpdateOrgChart = (companyId: number) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation({
    mutationFn: (payload: BulkOrgChartUpdate) =>
      UserAPI.bulkUpdateOrgChart(payload),
    onMutate: () => hanldeMutate(queryClient),
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) =>
      handleOnError(
        err,
        _payload,
        context,
        queryClient,
        translate as Translate<string, string>,
      ),
    onSuccess: (data: User[]) => {
      const previous = queryClient.getQueryData<MembersResponse>(
        ALL_MEMBERS_QUERY_KEY,
      );

      let finalMembers = {};

      if (previous !== undefined) {
        finalMembers = data.reduce(
          (oldMembers, currentUser) =>
            patchUsersByPayload(oldMembers, currentUser),
          previous,
        );
        queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);
      }

      if (isEmpty(finalMembers)) {
        queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      }
      queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
      queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
    },
  });
};

export const useReplaceUserOrgChart = (companyId: number) => {
  const queryClient = useQueryClient();
  const translate = useTranslate();

  return useMutation({
    mutationFn: ({ oldUser, newUser }: { oldUser: number; newUser: number }) =>
      UserAPI.replaceUserInOrgChart(oldUser, newUser),
    onMutate: () => hanldeMutate(queryClient),
    onError: (
      err: Record<string, Record<string, unknown>>,
      _payload,
      context,
    ) =>
      handleOnError(
        err,
        _payload,
        context,
        queryClient,
        translate as Translate<string, string>,
      ),
    onSuccess: (data, variables) => {
      const previous = queryClient.getQueryData<MembersResponse>(
        ALL_MEMBERS_QUERY_KEY,
      );
      const { oldUser, newUser } = variables;

      let finalMembers = {};

      const newUserIndex = previous?.members.findIndex(
        (member) => member.user.userId === newUser,
      );

      const oldUserIndex = previous?.members.findIndex(
        (member) => member.user.userId === oldUser,
      );

      if (newUserIndex && oldUserIndex && previous) {
        const newUserData = previous.members[newUserIndex].user;
        const oldUserData = previous.members[oldUserIndex].user;

        newUserData.reportingTo = oldUserData.reportingTo;
        newUserData.managerOf = oldUserData.managerOf;

        oldUserData.reportingTo = undefined;
        oldUserData.managerOf = undefined;

        const newUserPatchedData = patchUsersByPayload(previous, newUserData);

        finalMembers = patchUsersByPayload(newUserPatchedData, oldUserData);
      }

      queryClient.setQueryData(ALL_MEMBERS_QUERY_KEY, finalMembers);

      queryClient.invalidateQueries(getPeopleHierarchyQueryKey(companyId));
      queryClient.invalidateQueries(getPeopleListQueryKey(companyId));
    },
  });
};

export const useExternalTeamMemberGridToUpdate = (projectId) => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload: PartialMember) => MembersAPI.updateMember(payload),
    {
      onSuccess: async () => {
        // queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        await queryClient.invalidateQueries(
          getProjectMembersQueryKey(ProjectId(projectId)),
        );
      },
    },
  );
};

// Do not cache this query because it is used in a modal that is opened and closed frequently
// Also since the user search is a typeahead, we don't want to cache the results
// This query is used in the AssigneePicker and @mention components mostly
export const useFetchPaginatedMembersData = (
  params: FetchAssigneePickerDataParams,
  options?: Omit<
    UseQueryOptions<AssigneePickerData, unknown, AssigneePickerData, any[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery(
    [
      'paginated-members',
      params.projectId,
      params.assigneePickerFilter,
      params.isPrivate,
      params.includeRestricedPartnersMembers,
      params.searchString,
      params.userType,
      params.userStatus,
      params.taskStartDateTime,
      params.taskEndDateTime,
      params.fetchUnResolvedPlaceholdersOnly,
      params.groupId,
      params.includedUserIds,
      params.includedTeamIds,
      params.fieldFilters,
      params.startRow,
      params.endRow,
    ],
    () => MembersAPI.fetchPaginatedMembersData(params),
    {
      cacheTime: 0,
      ...options,
    },
  );
};

export const MEMBER_INTEGRATIONS_QUERY_KEY = ['member-integrations'];

export const useMemberIntegrationsQuery = (
  options?: Omit<
    UseQueryOptions<
      MemberIntegrationsResponse,
      unknown,
      MemberIntegrationsResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<MemberIntegrationsResponse> => {
  return useQuery(
    MEMBER_INTEGRATIONS_QUERY_KEY,
    () => MembersAPI.getMemberIntegrations(),
    {
      ...options,
    },
  );
};

export const useUpdateMemberIntegrations = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: UpdateMemberIntegrationsPayload) =>
      MembersAPI.updateMemberIntegrations(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
        queryClient.invalidateQueries(MEMBER_INTEGRATIONS_QUERY_KEY);
      },
    },
  );
};
